<script lang="ts" setup>
import type { ToasterProps } from "vue-sonner"
import { reactiveOmit, useMediaQuery } from "@vueuse/core"
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon, XIcon } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"
import { Toaster as Sonner } from "vue-sonner"

const props = withDefaults(defineProps<ToasterProps>(), {
  richColors: false,
  expand: false,
  closeButton: true,
})

// Toaster is mounted outside <ClientOnly> in consuming apps. SSR and the first client
// render must produce the same position, so we fall back to 'top-right' until mount —
// matching the mobile default — then switch to the media-query-driven value.
const mounted = ref(false)
onMounted(() => { mounted.value = true })
const isDesktop = useMediaQuery('(min-width: 1024px)')
const resolvedPosition = computed<ToasterProps['position']>(() => {
  if (props.position) return props.position
  if (!mounted.value) return 'top-right'
  return isDesktop.value ? 'bottom-right' : 'top-right'
})

const delegatedProps = reactiveOmit(props, "toastOptions", "position")

// Bail-out selector for elements whose own click behavior must be preserved:
// vue-sonner's close/action/cancel buttons, plus any interactive descendant a toast
// might render (links, form controls, role=button). Relying on these prevents
// accidental dismissal of nested interactive content AND prevents recursion when we
// programmatically click the close button (the synthetic click bubbles back up with
// target inside [data-close-button], which this selector matches).
const INTERACTIVE_BAIL_OUT = [
  '[data-close-button]',
  '[data-button]',
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'label',
  '[role="button"]',
  '[contenteditable="true"]',
].join(',')

function handleToastClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  if (!target) return
  if (target.closest(INTERACTIVE_BAIL_OUT)) return
  const toastEl = target.closest<HTMLElement>('[data-sonner-toast]')
  if (!toastEl) return
  const closeBtn = toastEl.querySelector<HTMLButtonElement>('[data-close-button]')
  closeBtn?.click()
}
</script>

<template>
  <Sonner
    class="toaster group"
    v-bind="delegatedProps"
    :position="resolvedPosition"
    @click="handleToastClick"
  >
    <template #success-icon>
      <CircleCheckIcon class="size-4 text-[#62c0be]" />
    </template>
    <template #info-icon>
      <InfoIcon class="size-4 text-gray-400" />
    </template>
    <template #warning-icon>
      <TriangleAlertIcon class="size-4 text-[#ff9812]" />
    </template>
    <template #error-icon>
      <OctagonXIcon class="size-4 text-red-400" />
    </template>
    <template #loading-icon>
      <Loader2Icon class="size-4 animate-spin text-gray-400" />
    </template>
    <template #close-icon>
      <XIcon class="size-3" />
    </template>
  </Sonner>
</template>
